import { identityService } from "../profile/identityService_v2.js";
import { identitySearchService } from "../profile/identitySearchService_v2.js";
import { escapeHtml } from "../escapeHtml_v2.js";
import { dasModal } from "../modal_v2.js";
import { colors } from "../colors_v2.js";
import { newMatch } from "./newMatch_v2.js";

import { eventHub } from "../eventHub_v2.js";
import { iPop } from "../ipop_v2.js";
import {
  NAME_MAX_LENGTH,
  FORBIDDEN_CHARS,
  BANNED_WORDS,
} from "../constants_v2.js";

const searchNameState = {
  sendToModule: "",
  player: "",
  searchResults: [],
};

/**
 * @brief Renders the modal search name pop up, which can be used to find and select an
 * identity by any part of the name field (2 char min).
 * @param  input object with two keys:
 * * sendToModule: "name of module that wants the result",
 * * player: "a string that module uses to identify who is playing first or second",
 * @returns {{status: number, identity: object, message: string}} Returns an object with these keys:
 * * status: Could be 200 or 404
 * * identity: When the status is 200, this contains the full identity object. Otherwise, it's empty.
 * * message: When the status is not 200, this is the error message
 */
const render = (input) => {
  const keys = Object.keys(input);
  if (!keys.includes("sendToModule")) {
    const message = "ERROR: The input is missing sendToModule";
    return { status: 400, message };
  }
  if (!keys.includes("player")) {
    const message = "ERROR: The input is missing player";
    return { status: 400, message };
  }
  const popBehinder = document.getElementById("popBehinder");
  popBehinder.dataset.action = "popBehinderCloseSearchNamePop";
  popBehinder.style.display = "block";

  searchNameState.sendToModule = input.sendToModule;
  searchNameState.player = input.player;

  const html = `
      <div class="popUpHead" id="searchNameHeader">Search<div id="searchSplainer" class="i">ⓘ</div></div>
        
      <label for="searchNamePopInput">
        <input type="text" id="searchNamePopInput"></input>
        <div class="courier" id="searchNamePopMsg"></div>
      </label>
      <div id="twoSearches">Enter part of their name, blurb, or rank<div id="containsSplainer" class="i">ⓘ</div></div>
      <div id="searchLRContainer" >
        <div id="searchRight" class="searchRight">
          <button class="searchButton" id="searchContains">Search</button>
          <div class="courier" id="searchContainsMsg"></div>
        </div>
      </div>
      <div id="searchNamePopResultsWrapper">
      Select Name:
        <div class="courier" id="searchNamePopResults"></div>
      </div>
      `;
  dasModal.render(html);
  const searchNamePopInput = document.getElementById("searchNamePopInput");
  searchNamePopInput.style.backgroundColor =
    colors.getGameColor("lighterAccent");
  searchNamePopInput.focus();
  document.getElementById("searchNamePopResultsWrapper").style.borderColor =
    colors.getGameColor("hexGrid");
  colors.setButtonColors();
  document.getElementById("searchRight").style.backgroundColor =
    colors.getGameColor("tip");
  return { status: 200 };
};

const renderJoinCodeSplainer = (event) => {
  iPop.render({
    text: "Join Codes are found under Current Match",
    event,
  });
};

const renderSearchSplainer = (event) => {
  iPop.render({
    text: `<div class="max">You can search for accounts, but not temporary identities.</p>`,
    event,
  });
};

const renderContainsSplainer = (event) => {
  iPop.render({
    text: `Not case sensitive.`,
    event,
  });
};

const handleKeyUp = async (event) => {
  const text = String(document.getElementById("searchNamePopInput").value);
  const messageElement = document.getElementById("searchNamePopMsg");
  const result = identityService.isSearchValid(text);
  if (result.status != 200) {
    messageElement.innerText = result.message;
    return;
  }
  // if they got through all that, then
  messageElement.innerText = "";
};

const handleSearchNameButton = async (event, element) => {
  const gotClicked = element.id;
  let messageElement = null;
  let type = null;
  if (gotClicked === "searchBeginsWith") {
    messageElement = document.getElementById("searchBeginsWithMsg");
    type = "beginsWith";
  }
  if (gotClicked === "searchContains") {
    messageElement = document.getElementById("searchContainsMsg");
    type = "contains";
  }
  if (type === null) {
    return;
  }

  const text = document.getElementById("searchNamePopInput").value;
  const result = identityService.isSearchValid(text);
  if (result.status != 200) {
    messageElement.innerText = result.message;
    return;
  }
  messageElement.innerText = "Searching...";

  let searchResult;

  try {
    document.body.style.cursor = "wait";
    searchResult = await identitySearchService.searchIdentities({
      characters: text,
      type,
    });
    if (searchResult.status != 200) {
      messageElement.innerText = searchResult.message;
      return;
    }

    var html = ``;
    searchNameState.searchResults = searchResult.identities;
    for (const identity of searchResult.identities) {
      var blurb = "Not Entered";
      if (Object.keys(identity).includes("blurb")) {
        blurb = identity.blurb;
      }
      var rank = identity?.rank ?? "0000";
      var rank = rank.slice(0, 4);

      // SECURITY: names, blurbs and ranks are other users' data. Escape them,
      // and quote the attribute. The id is the record's userID (what
      // handleNameClick matches on), not one parsed from the name.
      html =
        html +
        `<div id="${escapeHtml(identity.userID)}" class="searchNameDiv">${escapeHtml(identity.name)}
          <br><span class="arial">Blurb:</span> <span class="courier">"${escapeHtml(blurb)}"</span>
          <br><span class="arial">Rank:</span> <span class="courier">${escapeHtml(rank)}</span> 
          </div>`;
    }
    if (searchResult.identities.length === 0) {
      html = `<div class="max">No accounts found</div>`;
    }
    document.getElementById("searchNamePopResults").innerHTML = html;
    const searchNameDivs = document.getElementsByClassName("searchNameDiv");
    for (const searchNameDiv of searchNameDivs) {
      searchNameDiv.style.backgroundColor = colors.getGameColor("tip");
      eventHub.registerEvent("click", searchNameDiv.id, handleNameClick);
      eventHub.registerEvent("pointerover", searchNameDiv.id, () => {
        searchNameDiv.style.backgroundColor =
          colors.getGameColor("lighterAccent");
      });
      eventHub.registerEvent("pointerout", searchNameDiv.id, () => {
        searchNameDiv.style.backgroundColor = colors.getGameColor("tip");
      });
    }
  } catch (error) {
    console.error("Search failed:", error);
  } finally {
    document.body.style.cursor = "default";
    messageElement.innerText = "";
  }
};

const handleNameClick = (event, element) => {
  const container = event.target.closest(".searchNameDiv");
  if (!container) {
    return;
  }

  var gotClickedIdentity = {};
  for (const identity of searchNameState.searchResults) {
    if (container.id === identity.userID) {
      gotClickedIdentity = identity;
      break;
    }
  }
  if (Object.keys(gotClickedIdentity).length === 0) {
    return;
  }
  const params = {
    sendToModule: searchNameState.sendToModule,
    player: searchNameState.player,
    name: gotClickedIdentity.name,
  };
  const result = newMatch.setInviteeName(params);

  if (result.status != 200) {
    document.getElementById("searchNamePopMsg").textContent = result.message;
  }
  if (result.status === 200) {
    clearSearchNameState();
    close();
  }

  dasModal.render(`
      <h3 id="searchNameHeader">What's Next:</h3>
      <p>After you create the match:</p>
      <p>If their email notifications are on, they will get an email.</p> 
      <p>If their new match pop up is on, they will also get an alert if they are logged in.</p>
      <br>
      `);
};

const clearSearchNameState = () => {
  searchNameState.sendToModule = "";
  searchNameState.player = "";
  searchNameState.name = "";
  searchNameState.searchResults = "";
};

const close = () => {
  const popBehinder = document.getElementById("popBehinder");
  popBehinder.style.display = "none";
  delete popBehinder.dataset.action;
  dasModal.close();
};

// Enter in the search box does the same as clicking Search.
const handleSearchInputKeydown = (event) => {
  if (event.key !== "Enter") return;
  if (event.isComposing || event.keyCode === 229) return;
  event.preventDefault();
  const searchButton = document.getElementById("searchContains");
  if (!searchButton || searchButton.disabled) return;
  handleSearchNameButton(event, searchButton);
};

eventHub.registerEvent("keyup", "searchNamePopInput", handleKeyUp);
eventHub.registerEvent("keydown", "searchNamePopInput", handleSearchInputKeydown);
eventHub.registerEvent("click", "searchBeginsWith", handleSearchNameButton);
eventHub.registerEvent("click", "searchContains", handleSearchNameButton);
eventHub.registerEvent("click", "popBehinderCloseSearchNamePop", close);
eventHub.registerEvent("click", "joinCodeSplainer", renderJoinCodeSplainer);
eventHub.registerEvent("click", "searchSplainer", renderSearchSplainer);
eventHub.registerEvent("click", "containsSplainer", renderContainsSplainer);

export const searchName = {
  render,
  close,
};
